import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { createLogger } from "logging";
import { PropsWithChildren, useMemo } from "react";

import { AuthenticationContext, defaultScopes, IAuthenticationContext } from "./AuthenticationContext";
import { AuthLoading } from "./AuthLoading";

const logger = createLogger("AuthenticatedProvider");

export const AuthenticatedProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { accounts, instance } = useMsal();

  const authentication = useMemo<IAuthenticationContext | undefined>(() => {
    if (accounts.length === 0 || !instance) {
      return undefined;
    }

    const account = accounts[0];

    return {
      currentUser: {
        name: account.name || account.username,
        username: account.username,
        id: account.localAccountId,
      },

      acquireTokenSilent: async (scopes: string[] = defaultScopes) => {
        logger.trace(`acquireTokenSilent(): requesting for ${scopes.join(",")} ...`);

        try {
          const response = await instance.acquireTokenSilent({ scopes, account });
          logger.trace(`acquireTokenSilent(): got ${response.accessToken.substring(0, 10)} for ${scopes.join(",")}`);
          return response.accessToken;
        } catch (error) {
          logger.warn("Silent token acquisition failed:", error);

          if (error instanceof InteractionRequiredAuthError) {
            logger.info("Interaction required, triggering login redirect");
            await instance.loginRedirect({ scopes });
            throw new Error("Redirecting to login", { cause: error });
          }

          // Retry with forceRefresh for other errors
          try {
            logger.info("Retrying with forceRefresh=true");
            const response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
            return response.accessToken;
          } catch (retryError) {
            logger.error("Token acquisition retry failed, triggering login redirect:", retryError);
            await instance.loginRedirect({ scopes });
            throw new Error("Redirecting to login", { cause: retryError });
          }
        }
      },
      logout: () => {
        logger.debug("logout(): Logging out");
        instance.logoutRedirect();
      },
    };
  }, [accounts, instance]);

  if (!authentication) {
    return <AuthLoading />;
  }

  return <AuthenticationContext.Provider value={authentication}>{children}</AuthenticationContext.Provider>;
};
